import React, { useState, useEffect, useRef } from 'react';
import { WebsiteDesignProps } from './types';
import { 
  Trophy, Medal, Timer, ChevronRight, ArrowRight, ArrowUpRight, 
  Menu, X, Zap, Heart, Users, School, Clock, 
  MapPin, Phone, Mail, Globe, GraduationCap, ChevronLeft, 
  ChevronDown, Download, Send, Plus, Layout, Sparkles, 
  FileText, Instagram, Facebook, Linkedin, Youtube, Quote, BookOpen, Star, Camera, Shield, Activity
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Button } from "@/components/ui/button";
import { VisualImageEditable } from "@/components/VisualImageEditable";
import { VisualTextEditable } from "@/components/VisualTextEditable";

const SportingExcellence: React.FC<WebsiteDesignProps> = ({ previewData, isEditMode, onUpdateField }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Icon resolver helper
  const getIcon = (name: string | any) => {
      if (!name) return Trophy;
      if (typeof name !== 'string') return name;
      // @ts-ignore
      const Icon = LucideIcons[name];
      return Icon || Trophy;
  };

  const schoolName = previewData?.name || "Apex Sports High";

  // Shared Data Model (Strict Matching Design 1)
  const defaultData = {
    hero: {
      images: ["https://images.unsplash.com/photo-1517649763962-0c623066013b?q=80&w=2070&auto=format&fit=crop"],
      tagline: "CHAMPIONS ARE MADE HERE",
      titlePrefix: "TRAIN. COMPETE.",
      subtitle: "The premier destination for student-athletes. Developing elite competitors and superior scholars through rigorous training and academic discipline."
    },
    features: [
      { icon: "Timer", title: "ELITE SPEED", desc: "Specialized performance coaching for track and explosive power sports." },
      { icon: "Trophy", title: "CHAMPIONSHIP MENTALITY", desc: "Psychological resilience training for high-pressure competition." },
      { icon: "Activity", title: "RECOVERY LABS", desc: "Pro-grade physiotherapy and nutritional science integration." }
    ],
    programs: [
      { title: "ATHLETIC ACADEMY", age: "YEARS 7-13", desc: "Intensive training protocols for Olympic-track student athletes.", img: "https://images.unsplash.com/photo-1526676023131-d352423b036c?q=80&w=2070&auto=format&fit=crop" },
      { title: "TACTICAL LEADERSHIP", age: "COLLEGE", desc: "Sports management and coaching certification pathways.", img: "https://images.unsplash.com/photo-1542382257-80dedb725088?q=80&w=2128&auto=format&fit=crop" }
    ],
    stats: [
        { value: "48", label: "GOLD MEDALS" },
        { value: "12", label: "PRO SIGNINGS" },
        { value: "0", label: "LOSSES THIS SEASON" }
    ],
    news: [
        { title: "STADIUM UNVEILED", date: "FEB 12", desc: "Our new 5,000-seat multi-sport stadium is officially open.", img: "https://images.unsplash.com/photo-1504450758481-7338eba7524a?q=80&w=2069&auto=format&fit=crop" }
    ],
    nav: [
        { label: 'ATHLETICS', href: '#features' },
        { label: 'TRAINING', href: '#programs' },
        { label: 'HALL OF FAME', href: '#gallery' },
        { label: 'COACHING', href: '#team' }
    ],
    team: [
        { name: "Coach Mike Irons", role: "Head Athletic Director", img: "https://images.unsplash.com/photo-1534030347209-467a5b0ad3e6?q=80&w=1974&auto=format&fit=crop" },
        { name: "Dr. Elena Vance", role: "Sports Psychologist", img: "https://images.unsplash.com/photo-1594824476967-48c8b964273f?q=80&w=1974&auto=format&fit=crop" }
    ],
    gallery: [
        { category: "Match Day", img: "https://images.unsplash.com/photo-1461896756913-647528ab40e7?q=80&w=2070&auto=format&fit=crop", title: "Final Lap" },
        { category: "Training", img: "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?q=80&w=2070&auto=format&fit=crop", title: "Iron Hive" }
    ],
    facilities: [
        { title: "THE ARENA", desc: "World-class competition floor with VAR technology.", img: "https://images.unsplash.com/photo-1541252260730-0412e8e2108e?q=80&w=2153&auto=format&fit=crop", icon: "Trophy" },
        { title: "AQUATIC CENTER", desc: "Olympic-sized pool with underwater motion analysis.", img: "https://images.unsplash.com/photo-1530549387631-fca129c15248?q=80&w=2070&auto=format&fit=crop", icon: "Droplets" }
    ]
  };

  const mockData = {
      ...previewData,
      hero: {
          titlePrefix: previewData?.hero?.titlePrefix || defaultData.hero.titlePrefix,
          tagline: previewData?.hero?.tagline || defaultData.hero.tagline,
          subtitle: previewData?.hero?.subtitle || defaultData.hero.subtitle,
          images: previewData?.hero?.images || defaultData.hero.images,
          backgroundImage: previewData?.hero?.backgroundImage || defaultData.hero.images[0]
      },
      about: {
          title: previewData?.about?.title || "WINNING IS IN OUR DNA.",
          description: previewData?.about?.description || schoolName + " is not just a school. It is an incubator for greatness. We believe that the discipline learned on the field is the foundation for success in life.",
          sectionImage: previewData?.about?.sectionImage || "https://images.unsplash.com/photo-1512716676650-59796924b15f?q=80&w=2070&auto=format&fit=crop"
      },
      features: (previewData?.features || defaultData.features).map((f: any) => ({ 
        ...f, 
        icon: getIcon(f.icon),
        desc: f.description || f.desc || ""
      })),
      programs: (previewData?.programs || defaultData.programs).map((p: any) => ({
        title: p.name || p.title || "",
        age: p.age || "",
        desc: p.description || p.desc || "",
        img: p.image || p.img || ""
      })),
      stats: (previewData?.stats || defaultData.stats).map((s: any) => ({
          value: s.value || "0",
          label: s.label || ""
      })),
      news: (previewData?.news || defaultData.news).map((n: any) => ({
        title: n.title || "",
        date: n.date || "",
        desc: n.description || n.desc || "",
        img: n.image || n.img || ""
      })),
      team: (previewData?.team || defaultData.team).map((t: any) => ({
        name: t.name || "",
        role: t.role || "",
        img: t.img || t.image || "",
        linkedin: t.linkedin || "",
        email: t.email || ""
      })),
      gallery: (previewData?.gallery || defaultData.gallery).map((g: any) => ({
        category: g.category || "General",
        title: g.caption || g.title || "",
        img: g.image || g.img || ""
      })),
      facilities: (previewData?.facilities || defaultData.facilities).map((f: any) => ({ 
        ...f, 
        icon: getIcon(f.icon),
        desc: f.description || f.desc || "",
        img: f.image || f.img || ""
      })),
      downloads: (previewData?.downloads || [
          { title: "Athletic_Scouting_Pack.pdf", size: "8.2 MB", type: "PDF" },
          { title: "Season_Schedules_2026.pdf", size: "1.5 MB", type: "PDF" }
      ]).map((d: any) => ({
          title: d.title || "",
          size: d.size || "",
          type: d.type || "PDF"
      })),
      contact: {
        address: previewData?.contact?.address || "STADIUM ROAD, SPORTS HUB, NAIROBI",
        phone: previewData?.contact?.phone || "+254 111 222 333",
        email: previewData?.contact?.email || "scouting@apexhigh.edu",
        officeHours: previewData?.contact?.officeHours || "0500 - 2200 // MON - SUN",
        facebook: previewData?.contact?.facebook || "",
        instagram: previewData?.contact?.instagram || "",
        youtube: previewData?.contact?.youtube || "",
        linkedin: previewData?.contact?.linkedin || ""
      },
      sectionVisibility: {
          hero: previewData?.sectionVisibility?.hero !== false,
          about: previewData?.sectionVisibility?.about !== false,
          stats: previewData?.sectionVisibility?.stats !== false,
          features: previewData?.sectionVisibility?.features !== false,
          programs: previewData?.sectionVisibility?.programs !== false,
          facilities: previewData?.sectionVisibility?.facilities !== false,
          team: previewData?.sectionVisibility?.team !== false,
          gallery: previewData?.sectionVisibility?.gallery !== false,
          news: previewData?.sectionVisibility?.news !== false,
          downloads: previewData?.sectionVisibility?.downloads !== false,
          cta: previewData?.sectionVisibility?.cta !== false,
          contact: previewData?.sectionVisibility?.contact !== false
      },
      nav: (previewData?.nav || defaultData.nav).map((n: any) => ({
          label: n.label || "",
          href: n.href || "#",
          visible: n.visible !== false
      })),
      labels: {
          downloadsSection: previewData?.labels?.downloadsSection || "Playbooks & Kits",
          downloadsTitle: previewData?.labels?.downloadsTitle || "Athlete Resources",
          contactFormTitle: previewData?.labels?.contactFormTitle || "Contact Form",
          contactFormSubtitle: previewData?.labels?.contactFormSubtitle || "Fill out the form below."
      }
  };

  useEffect(() => {
    const doc = containerRef.current?.ownerDocument || document;
    const root = doc.documentElement;
    if (previewData?.theme) {
        const { colors, fonts } = previewData.theme;
        root.style.setProperty('--color-primary', colors.primary || '#ff4500');
        root.style.setProperty('--color-secondary', colors.secondary || '#111111');
        root.style.setProperty('--color-accent', colors.accent || '#ffffff');
        root.style.setProperty('--color-background', colors.background || '#ffffff');
        root.style.setProperty('--color-text', colors.text || '#111111');
        root.style.setProperty('--font-heading', `'${fonts.heading || 'Archivo'}', sans-serif`);
        root.style.setProperty('--font-body', `'${fonts.body || 'Inter'}', sans-serif`);
    }
    const fontLink = doc.createElement('link');
    fontLink.href = "https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,900;1,900&family=Inter:wght@400;700;900&display=swap";
    fontLink.rel = 'stylesheet';
    doc.head.appendChild(fontLink);
  }, [previewData?.theme]);

  const handleUpdate = (path: string, val: any) => onUpdateField && onUpdateField(path, val);
  const addArrayItem = (path: string, newItem: any) => {
    if (onUpdateField) {
        const arr = [...((mockData as any)[path] || [])];
        onUpdateField(path, [...arr, newItem]);
    }
  };
  const removeArrayItem = (path: string, index: number) => {
    if (onUpdateField && window.confirm('Cut from the roster?')) {
        const arr = [...((mockData as any)[path] || [])];
        onUpdateField(path, arr.filter((_, i) => i !== index));
    }
  };

  return (
    <div ref={containerRef} className="sporting-excellence bg-[var(--color-background)] text-[var(--color-text)] min-h-screen font-[var(--font-body)] selection:bg-[var(--color-primary)] selection:text-white overflow-x-hidden">
      
      {/* Dynamic Header */}
      <div className="bg-[var(--color-primary)] text-white py-2 px-6 text-center text-[10px] font-black uppercase tracking-[0.4em] italic hidden md:block border-b-4 border-black/20">
          PRO-PELT // ELITE SCOUTING ACTIVE // SESSION_2026/27
      </div>

      <nav className="container mx-auto px-6 py-6 flex justify-between items-center relative z-50">
          <div className="group cursor-pointer">
              <h1 className="text-4xl md:text-5xl font-[var(--font-heading)] italic font-black uppercase tracking-tighter leading-none skew-x-[-10deg]">
                  <VisualTextEditable value={schoolName} onSave={(v) => handleUpdate('name', v)} isEditMode={!!isEditMode} />
              </h1>
          </div>
          
          <div className="hidden lg:flex items-center gap-12">
              <div className="flex gap-10 text-[11px] font-black uppercase tracking-widest italic skew-x-[-10deg]">
                  {mockData.nav.filter(n => n.visible).map((n, i) => (
                      <a key={i} href={n.href} className="hover:text-[var(--color-primary)] transition-all">
                          {n.label}
                      </a>
                  ))}
              </div>
              <Button className="bg-black text-white font-black rounded-none px-10 h-14 text-[10px] uppercase tracking-widest italic skew-x-[-15deg] hover:bg-[var(--color-primary)] transition-all shadow-[8px_8px_0px_var(--color-primary)]">
                  RECRUIT ME
              </Button>
          </div>

          <button className="lg:hidden" onClick={() => setMenuOpen(!menuOpen)}>
              {menuOpen ? <X size={32} /> : <Menu size={32} />}
          </button>
      </nav>

      {/* Hero Section - The Blast */}
      {mockData.sectionVisibility.hero && (
      <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-[var(--color-secondary)]">
          {/* Slanted Background */}
          <div className="absolute top-0 right-0 w-2/3 h-full bg-[var(--color-primary)] opacity-10 skew-x-[-20deg] transform translate-x-32" />
          <div className="absolute top-0 right-[-10%] w-[60%] h-full group">
               <VisualImageEditable 
                  src={mockData.hero.backgroundImage} 
                  className="w-full h-full object-cover skew-x-[-10deg] scale-125 opacity-60 group-hover:opacity-100 group-hover:scale-110 transition-all duration-[3s] grayscale mix-blend-luminosity" 
                  alt="Athlete"
                  onImageChange={(u) => handleUpdate('hero.backgroundImage', u)}
                  isEditMode={!!isEditMode}
              />
          </div>

          <div className="container mx-auto px-6 relative z-10 text-white">
              <div className="animate-in slide-in-from-left duration-700">
                  <div className="inline-block px-6 py-2 bg-[var(--color-primary)] text-white text-[11px] font-black uppercase tracking-[0.4em] italic mb-8 skew-x-[-15deg]">
                    <VisualTextEditable value={mockData.hero.tagline} onSave={(v) => handleUpdate('hero.tagline', v)} isEditMode={!!isEditMode} />
                  </div>
                  <h2 className="text-7xl md:text-[12rem] font-[var(--font-heading)] font-black uppercase leading-[0.8] mb-12 tracking-tighter skew-x-[-5deg]">
                      <VisualTextEditable value={mockData.hero.titlePrefix} onSave={(v) => handleUpdate('hero.titlePrefix', v)} isEditMode={!!isEditMode} /><br/>
                      <span className="text-[var(--color-primary)]">WIN.</span>
                  </h2>
                  <p className="text-xl md:text-3xl font-black uppercase italic tracking-wide max-w-xl mb-16 opacity-60 leading-tight">
                      <VisualTextEditable value={mockData.hero.subtitle} onSave={(v) => handleUpdate('hero.subtitle', v)} isEditMode={!!isEditMode} multiline />
                  </p>
                  <div className="flex flex-wrap gap-8">
                      <Button className="h-20 px-16 bg-[var(--color-primary)] text-white rounded-none font-black uppercase italic tracking-[0.2em] text-lg skew-x-[-15deg] hover:translate-x-2 transition-transform shadow-[15px_15px_0px_#000]">
                          APPLY_NOW
                      </Button>
                      <button className="flex items-center gap-6 text-xs font-black uppercase tracking-[0.4em] italic group">
                          VIEW ROSTER <div className="w-20 h-0.5 bg-white group-hover:w-32 transition-all" />
                      </button>
                  </div>
              </div>
          </div>
          
          {/* Marquee Ticker */}
          <div className="absolute bottom-10 left-0 w-full bg-[var(--color-primary)] py-4 overflow-hidden -rotate-2 scale-110 border-y-4 border-black/10">
               <div className="flex animate-marquee whitespace-nowrap">
                   {[...Array(10)].map((_, i) => (
                       <span key={i} className="text-3xl font-black italic uppercase tracking-tighter text-white mx-12">UNDEFEATED SINCE 2018 // NATIONAL GOLD 2025 // APEX PRIDE //</span>
                   ))}
               </div>
          </div>
      </section>
      )}

      {/* High-Impact Features */}
      {mockData.sectionVisibility.features && (
      <section id="features" className="py-40 bg-white">
          <div className="container mx-auto px-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-4 border-black">
                  {mockData.features.map((item, i) => (
                      <div key={i} className={`p-16 group relative transition-all duration-500 overflow-hidden ${i % 2 === 0 ? 'bg-white text-black' : 'bg-black text-white hover:bg-[var(--color-primary)]'}`}>
                          <div className={`w-20 h-20 rounded-none flex items-center justify-center mb-10 skew-x-[-10deg] ${i % 2 === 0 ? 'bg-black text-white shadow-[8px_8px_0px_var(--color-primary)]' : 'bg-white text-black shadow-[8px_8px_0px_var(--color-secondary)]'}`}>
                              {React.createElement(item.icon, { size: 36 })}
                          </div>
                          <h3 className="text-4xl font-black uppercase font-[var(--font-heading)] mb-6 skew-x-[-5deg] leading-none">
                            <VisualTextEditable value={item.title} onSave={(v) => handleUpdate(`features[${i}].title`, v)} isEditMode={!!isEditMode} />
                          </h3>
                          <p className="text-lg font-bold uppercase opacity-60 leading-tight mb-10 italic">
                            <VisualTextEditable value={item.desc} onSave={(v) => handleUpdate(`features[${i}].desc`, v)} isEditMode={!!isEditMode} multiline />
                          </p>
                          <ArrowRight className="opacity-0 group-hover:opacity-100 group-hover:translate-x-4 transition-all" size={32} />
                          {isEditMode && <button onClick={() => removeArrayItem('features', i)} className="absolute top-4 right-4 text-[10px] font-black uppercase">Cut</button>}
                      </div>
                  ))}
              </div>
          </div>
      </section>
      )}

      {/* Program Grids - The Training Pipes */}
      {mockData.sectionVisibility.programs && (
      <section id="programs" className="py-40 bg-[var(--color-secondary)]">
          <div className="container mx-auto px-6">
              <div className="flex flex-col md:flex-row justify-between items-end mb-32 border-l-[20px] border-[var(--color-primary)] pl-12">
                  <h2 className="text-7xl md:text-9xl font-[var(--font-heading)] font-black uppercase text-white tracking-tighter italic">PROGRAMS.</h2>
                  <div className="text-[11px] font-black uppercase text-[var(--color-primary)] tracking-[0.5em] mb-4">ENGINEERING ATHLETES</div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                  {mockData.programs.map((p, i) => (
                      <div key={i} className="group relative aspect-[16/10] bg-white overflow-hidden skew-x-[-2deg] hover:skew-x-0 transition-all duration-500">
                           <VisualImageEditable 
                              src={p.img} alt={p.title}
                              className="absolute inset-0 w-full h-full object-cover grayscale brightness-50 group-hover:grayscale-0 group-hover:brightness-100 group-hover:scale-105 transition-all duration-1000"
                              onImageChange={(u) => handleUpdate(`programs[${i}].img`, u)}
                              isEditMode={!!isEditMode}
                          />
                           <div className="absolute inset-x-8 bottom-8 p-12 bg-black/60 backdrop-blur-md text-white border-l-8 border-[var(--color-primary)]">
                               <div className="text-[10px] font-black italic tracking-widest text-[var(--color-primary)] mb-4">{p.age}</div>
                               <h3 className="text-4xl md:text-6xl font-black uppercase font-[var(--font-heading)] italic mb-6 leading-none">
                                 <VisualTextEditable value={p.title} onSave={(v) => handleUpdate(`programs[${i}].title`, v)} isEditMode={!!isEditMode} />
                               </h3>
                               <p className="text-lg font-bold uppercase opacity-60 group-hover:opacity-100 transition-opacity">
                                 <VisualTextEditable value={p.desc} onSave={(v) => handleUpdate(`programs[${i}].desc`, v)} isEditMode={!!isEditMode} multiline />
                               </p>
                           </div>
                           {isEditMode && <button onClick={() => removeArrayItem('programs', i)} className="absolute top-4 right-4 z-20 text-xs font-black text-white p-2 bg-red-600">Suspend</button>}
                      </div>
                  ))}
                  {isEditMode && (
                      <div onClick={() => addArrayItem('programs', { title: "NEW TRACK", age: "ELITE", desc: "Performance module." })} className="aspect-[16/10] border-8 border-dashed border-white/10 flex flex-col items-center justify-center cursor-pointer hover:border-[var(--color-primary)] transition-all group">
                          <Plus size={80} className="text-white/10 group-hover:text-[var(--color-primary)]" />
                      </div>
                  )}
              </div>
          </div>
      </section>
      )}

      {/* Stats - Scoreboard */}
      {mockData.sectionVisibility.stats && (
      <section className="py-32 bg-[var(--color-primary)] text-white">
          <div className="container mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-24">
              {mockData.stats.map((s, i) => (
                  <div key={i} className="flex flex-col items-center text-center">
                      <div className="text-8xl md:text-[10rem] font-black italic skew-x-[-10deg] leading-none mb-6 drop-shadow-[10px_10px_0px_#000]">
                        <VisualTextEditable value={s.value} onSave={(v) => handleUpdate(`stats[${i}].value`, v)} isEditMode={!!isEditMode} />
                      </div>
                      <div className="text-xs font-black uppercase tracking-[0.8em] text-black">
                        <VisualTextEditable value={s.label} onSave={(v) => handleUpdate(`stats[${i}].label`, v)} isEditMode={!!isEditMode} />
                      </div>
                  </div>
              ))}
          </div>
      </section>
      )}

      {/* Team - The Coaching Staff */}
      {mockData.sectionVisibility.team && (
      <section id="team" className="py-40 bg-zinc-100 px-6">
          <div className="container mx-auto">
              <div className="flex flex-col md:flex-row justify-between items-start mb-32 border-b-8 border-black pb-8">
                  <h2 className="text-6xl md:text-9xl font-black italic uppercase italic skew-x-[-5deg]">COACHES.</h2>
                  <div className="max-w-xs text-right italic font-black text-gray-400">Tactical experts. Olympic mentors. Life-long champions.</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                  {mockData.team.map((m, i) => (
                      <div key={i} className="group relative skew-x-[-2deg] hover:skew-x-0 transition-transform">
                          <div className="aspect-[3/4] overflow-hidden mb-8 border-8 border-white shadow-2xl relative grayscale group-hover:grayscale-0 transition-all duration-700">
                               <VisualImageEditable 
                                  src={m.img} 
                                  alt={m.name} 
                                  className="w-full h-full object-cover" 
                                  onImageChange={(u) => handleUpdate(`team[${i}].img`, u)} 
                                  isEditMode={!!isEditMode}
                              />
                               <div className="absolute inset-0 border-[20px] border-black/0 group-hover:border-[var(--color-primary)]/20 transition-all" />
                          </div>
                          <div className="bg-black p-8 text-white skew-x-[-5deg] -mt-16 relative z-10 shadow-[10px_10px_0px_var(--color-primary)]">
                              <h3 className="text-3xl font-black uppercase italic leading-none mb-2">
                                  <VisualTextEditable value={m.name} onSave={(v) => handleUpdate(`team[${i}].name`, v)} isEditMode={!!isEditMode} />
                              </h3>
                              <p className="text-[10px] font-black uppercase tracking-widest text-[var(--color-primary)]">
                                  <VisualTextEditable value={m.role} onSave={(v) => handleUpdate(`team[${i}].role`, v)} isEditMode={!!isEditMode} />
                              </p>
                          </div>
                          {isEditMode && <button onClick={() => removeArrayItem('team', i)} className="text-red-600 font-black text-[9px] mt-6 uppercase">Drop Coach</button>}
                      </div>
                  ))}
                  {isEditMode && (
                      <div onClick={() => addArrayItem('team', { name: "NEW COACH", role: "SPECIALIST", img: "" })} className="aspect-[3/4] border-8 border-dashed border-black/10 flex items-center justify-center cursor-pointer hover:bg-black/5 group">
                          <Plus size={64} className="text-black/10 group-hover:text-black" />
                      </div>
                  )}
              </div>
          </div>
      </section>
      )}

      {/* Gallery - Action Shots */}
      {mockData.sectionVisibility.gallery && (
      <section id="gallery" className="grid grid-cols-1 md:grid-cols-3 gap-0 bg-black">
          {mockData.gallery.map((item, i) => (
              <div key={i} className="relative aspect-[3/4] group overflow-hidden border border-white/5">
                  <VisualImageEditable 
                      src={item.img} alt={item.title}
                      className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-110 transition-all duration-1000"
                      onImageChange={(u) => handleUpdate(`gallery[${i}].img`, u)}
                      isEditMode={!!isEditMode}
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity p-12 flex flex-col justify-end">
                      <div className="bg-[var(--color-primary)] text-white self-start px-4 py-2 text-[10px] font-black uppercase skew-x-[-15deg] mb-6 shadow-xl">{item.category}</div>
                      <h4 className="text-5xl font-black italic text-white uppercase leading-[0.7] tracking-tighter skew-x-[-10deg]">{item.title}</h4>
                  </div>
              </div>
          ))}
          {isEditMode && (
              <div onClick={() => addArrayItem('gallery', { category: "Action", title: "New Moment", img: "" })} className="aspect-[3/4] flex items-center justify-center border-2 border-white/10 cursor-pointer hover:bg-white/5">
                  <Camera size={64} className="text-white/10" />
              </div>
          )}
      </section>
      )}

      {/* Resource Center */}
      {mockData.sectionVisibility.downloads && (
      <section className="py-40 bg-black text-white px-6 overflow-hidden relative">
          <div className="absolute top-0 left-0 w-full h-2 bg-[var(--color-primary)]" />
          <div className="container mx-auto">
              <div className="flex flex-col md:flex-row justify-between items-end mb-24 relative z-10">
                  <div>
                      <h2 className="text-7xl md:text-9xl font-black italic uppercase tracking-tighter skew-x-[-5deg]">
                          <VisualTextEditable 
                              value={mockData.labels.downloadsTitle} 
                              onSave={(v) => handleUpdate('labels.downloadsTitle', v)} 
                              isEditMode={!!isEditMode} 
                          />
                      </h2>
                      <div className="text-[11px] font-black uppercase text-[var(--color-primary)] tracking-[0.5em] mt-4">
                          <VisualTextEditable 
                              value={mockData.labels.downloadsSection} 
                              onSave={(v) => handleUpdate('labels.downloadsSection', v)} 
                              isEditMode={!!isEditMode} 
                          />
                      </div>
                  </div>
                  {isEditMode && (
                      <Button onClick={() => addArrayItem('downloads', { title: "NEW_REPORT.PDF", size: "1.0 MB", type: "PDF" })} className="bg-white text-black font-black rounded-none px-10 h-14 text-[10px] uppercase tracking-widest italic skew-x-[-15deg] hover:bg-[var(--color-primary)] hover:text-white transition-all">
                          ADD_FILE +
                      </Button>
                  )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {mockData.downloads.map((doc, i) => (
                      <div key={i} className="group relative bg-zinc-900 border-l-8 border-[var(--color-primary)] p-10 skew-x-[-2deg] hover:skew-x-0 hover:bg-zinc-800 transition-all duration-500">
                          <div className="flex justify-between items-start mb-8">
                              <div className="w-16 h-16 bg-black flex items-center justify-center skew-x-[-10deg] group-hover:bg-[var(--color-primary)] transition-colors">
                                  <Download size={32} className="text-white" />
                              </div>
                              <div className="text-right">
                                  <div className="text-[10px] font-black text-[var(--color-primary)] mb-1">
                                      <VisualTextEditable value={doc.type} onSave={(v) => handleUpdate(`downloads[${i}].type`, v)} isEditMode={!!isEditMode} />
                                  </div>
                                  <div className="text-[10px] font-black text-white/20">
                                      <VisualTextEditable value={doc.size} onSave={(v) => handleUpdate(`downloads[${i}].size`, v)} isEditMode={!!isEditMode} />
                                  </div>
                              </div>
                          </div>
                          <h4 className="text-2xl font-black uppercase italic tracking-tight mb-8 leading-none group-hover:text-[var(--color-primary)] transition-colors">
                              <VisualTextEditable value={doc.title} onSave={(v) => handleUpdate(`downloads[${i}].title`, v)} isEditMode={!!isEditMode} />
                          </h4>
                          <button className="w-full py-4 bg-white/5 border border-white/10 text-[10px] font-black uppercase italic tracking-widest hover:bg-white hover:text-black transition-all flex items-center justify-center gap-4">
                              DOWNLOAD_REPORT <ArrowUpRight size={16} />
                          </button>
                          {isEditMode && (
                              <button onClick={() => removeArrayItem('downloads', i)} className="absolute -top-3 -right-3 w-8 h-8 bg-black text-white border border-red-600 flex items-center justify-center hover:bg-red-600 transition-all z-20">
                                  <X size={16} />
                              </button>
                          )}
                      </div>
                  ))}
              </div>
          </div>
          {/* Background Text Decor */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[25rem] font-black text-white/[0.02] tracking-tighter uppercase select-none pointer-events-none skew-x-[-10deg]">FILES</div>
      </section>
      )}

      {/* CTA - Draft Night */}
      {mockData.sectionVisibility.cta && (
      <section className="py-64 relative bg-black text-white text-center px-6 overflow-hidden">
          <div className="absolute top-0 right-0 w-full h-full bg-[#ff4500] opacity-10 skew-x-[-30deg] transform translate-x-1/2" />
          <div className="container mx-auto relative z-10 max-w-6xl">
              <h2 className="text-8xl md:text-[14rem] font-black italic tracking-tighter leading-[0.7] mb-20 uppercase shadow-2xl skew-x-[-10deg]">
                 ARE YOU <br/> <span className="text-[var(--color-primary)]">READY?</span>
              </h2>
              <div className="flex flex-col md:flex-row justify-center gap-12">
                   <Button className="bg-[var(--color-primary)] text-white h-24 md:h-32 px-16 font-black uppercase italic tracking-[0.2em] text-2xl skew-x-[-15deg] shadow-[20px_20px_0px_#fff] active:translate-x-2 active:translate-y-2 active:shadow-none transition-all">
                       SUBMIT_DRAFT()
                   </Button>
                   <Button variant="outline" className="border-4 border-white text-white h-24 md:h-32 px-16 font-black uppercase italic tracking-[0.2em] text-2xl skew-x-[-15deg] hover:bg-white hover:text-black transition-all">
                       VIEW_SCOUTS
                   </Button>
              </div>
          </div>
      </section>
      )}

      {/* Footer - Final Buzzer */}
      <footer id="contact" className="bg-black text-white pt-40 pb-20 px-6 relative overflow-hidden">
          <div className="container mx-auto relative z-10">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-24 mb-40 items-start">
                  <div className="lg:col-span-5">
                      <h2 className="text-7xl font-black italic uppercase tracking-tighter mb-12 skew-x-[-10deg]">
                        <VisualTextEditable value={schoolName} onSave={(v) => handleUpdate('name', v)} isEditMode={!!isEditMode} />
                      </h2>
                      <p className="text-2xl font-black italic uppercase leading-none text-white/20 mb-20">"Winning is not a sometime thing; it's an all the time thing."</p>
                      <div className="flex gap-6">
                          {[Facebook, Instagram, Youtube, Linkedin].map((Icon, i) => (
                              <a key={i} href="#" className="w-16 h-16 border-2 border-white/10 flex items-center justify-center hover:bg-[var(--color-primary)] hover:border-[var(--color-primary)] transition-all skew-x-[-10deg]"><Icon size={24} /></a>
                          ))}
                      </div>
                  </div>
                  
                  <div className="lg:col-span-2">
                      <h4 className="text-[11px] font-black tracking-[0.6em] uppercase text-[var(--color-primary)] mb-12 italic skew-x-[-10deg]">PLAYBOOK</h4>
                      <ul className="space-y-8 text-xl font-black uppercase italic tracking-tighter skew-x-[-5deg]">
                          {mockData.nav.map((item, i) => (
                              <li key={i}><a href={item.href} className="hover:text-[var(--color-primary)] transition-all flex items-center gap-10 group"><span>0{i+1}</span> {item.label}</a></li>
                          ))}
                      </ul>
                  </div>

                  <div className="lg:col-span-5">
                      <h4 className="text-[11px] font-black tracking-[0.6em] uppercase text-[var(--color-primary)] mb-12 italic skew-x-[-10deg]">COORDINATES</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                          <div className="space-y-6">
                              <MapPin size={40} className="text-[var(--color-primary)] mb-4" />
                              <p className="text-2xl font-black italic tracking-tighter uppercase leading-[0.85]">
                                  <VisualTextEditable value={mockData.contact.address} onSave={(v) => handleUpdate('contact.address', v)} isEditMode={!!isEditMode} multiline />
                              </p>
                          </div>
                          <div className="space-y-6">
                              <Phone size={40} className="text-[var(--color-primary)] mb-4" />
                              <p className="text-2xl font-black italic tracking-tighter uppercase leading-[0.85]">
                                  <VisualTextEditable value={mockData.contact.phone} onSave={(v) => handleUpdate('contact.phone', v)} isEditMode={!!isEditMode} />
                              </p>
                              <p className="text-xs font-black uppercase text-white/20 underline decoration-[var(--color-primary)]">
                                  <VisualTextEditable value={mockData.contact.email} onSave={(v) => handleUpdate('contact.email', v)} isEditMode={!!isEditMode} />
                              </p>
                          </div>
                      </div>
                  </div>
              </div>
              
              <div className="flex flex-col md:flex-row justify-between items-center gap-12 border-t border-white/5 pt-12 text-[10px] font-black uppercase tracking-[0.8em] text-white/10">
                  <p>&copy; {new Date().getFullYear()} {schoolName} // PRO_ELITE_LICENSE_09 // ARENA_MASTER</p>
                  <div className="flex gap-12 italic underline decoration-[var(--color-primary)]">
                      <a href="#" className="hover:text-white transition-colors">Tactical Charter</a>
                      <a href="#" className="hover:text-white transition-colors">Privacy Shield</a>
                  </div>
              </div>
          </div>
          {/* Background Text Decor */}
          <div className="absolute -bottom-20 -left-20 text-[30rem] font-black text-white/[0.02] tracking-tighter uppercase select-none pointer-events-none">APEX</div>
      </footer>

      {/* High-Velocity Full UI Overlay */}
      {menuOpen && (
          <div className="fixed inset-0 z-[1000] bg-black p-12 flex flex-col animate-in fade-in slide-in-from-bottom duration-500 overflow-hidden">
               <div className="absolute inset-0 bg-[var(--color-primary)] opacity-10 skew-x-[-45deg] transform translate-x-1/2" />
               <div className="relative z-10 flex flex-col h-full">
                   <div className="flex justify-between items-center mb-24 border-b border-white/10 pb-12">
                       <h2 className="text-5xl font-black italic uppercase tracking-tighter leading-none skew-x-[-10deg]">{schoolName}</h2>
                       <button onClick={() => setMenuOpen(false)} className="text-white"><X size={64} /></button>
                   </div>
                   <nav className="flex flex-col gap-12">
                       {mockData.nav.filter(n => n.visible).map((n, i) => (
                           <a key={i} href={n.href} onClick={() => setMenuOpen(false)} className="text-7xl md:text-[10rem] font-black uppercase tracking-tighter italic text-white/10 hover:text-[var(--color-primary)] hover:translate-x-12 transition-all skew-x-[-10deg]">
                               {n.label}
                           </a>
                       ))}
                   </nav>
               </div>
          </div>
      )}
    </div>
  );
};

export default SportingExcellence;
